import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, ExternalLink, TrendingUp, TrendingDown, Plus, Minus,
  Receipt, AlertTriangle, XCircle,
} from 'lucide-react';
import { billingApi, tenantAdminApi } from '../api/services';
import type { BillingStatus } from '../types/models';
import { formatPence, intervalLabel, planLabel } from '@/lib/billing';
import { useEntitlements } from '../context/EntitlementsContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LinearProgressBar } from '@/components/ui/linear-progress';
import { PageHeader } from '@/components/ui/page-header';
import { statusTone } from '@/lib/tones';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-GB').format(n);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

interface UsageData {
  year: number;
  month: number;
  totals: {
    planGenerations: number;
    workItemsCreated: number;
    transcriptsProcessed: number;
    imageAnalyses: number;
    aiTokensUsed: number;
    aiOperations?: number;
  };
  /** Organisation totals (per executive seat x seats); absent = no limit. */
  limits: {
    /** Also absent when the organisation is on its own AI key. */
    monthlyAiTokenLimit?: number;
    monthlyTranscriptLimit?: number;
    monthlyAiOperations?: number;
    executiveSeats?: number;
    transcriptsPerSeat?: number;
    aiOperationsPerSeat?: number;
  };
  aiProvider?: {
    source: 'tenant' | 'platform';
    provider?: 'Anthropic' | 'Foundry' | 'OpenAI';
    model?: string;
  };
}

function UsageBar({ label, current, limit, unlimitedNote }: { label: string; current: number; limit?: number; unlimitedNote?: string }) {
  const percent = limit && limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
  const isHigh = percent > 80;
  const isOverLimit = percent >= 100;

  return (
    <div>
      <div className="mb-1 flex justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span
          className={cn(
            'text-sm',
            isOverLimit ? 'text-rose-500' : isHigh ? 'text-amber-500' : 'text-muted-foreground',
          )}
        >
          {formatNumber(current)}{limit ? ` / ${formatNumber(limit)}` : unlimitedNote ? ` · ${unlimitedNote}` : ''}
        </span>
      </div>
      {limit != null && limit > 0 && (
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              'h-full rounded-full transition-[width]',
              isOverLimit ? 'bg-rose-500' : isHigh ? 'bg-amber-500' : 'bg-primary',
            )}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  );
}

function getProjection(current: number, limit: number | undefined): { projectedPercent: number; label: string } | null {
  if (!limit || limit <= 0 || current <= 0) return null;
  const now = new Date();
  const dayOfMonth = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  if (dayOfMonth < 2) return null;
  const dailyRate = current / dayOfMonth;
  const projected = dailyRate * daysInMonth;
  const projectedPercent = Math.round((projected / limit) * 100);
  return { projectedPercent, label: `Projected ${projectedPercent}% by month end` };
}

function QuotaWarningBars({ usage }: { usage: UsageData }) {
  const warnings: { label: string; percent: number; projected: string | null }[] = [];

  const aiLimit = usage.limits.monthlyAiTokenLimit;
  const transcriptLimit = usage.limits.monthlyTranscriptLimit;

  if (aiLimit && aiLimit > 0) {
    const pct = (usage.totals.aiTokensUsed / aiLimit) * 100;
    const proj = getProjection(usage.totals.aiTokensUsed, aiLimit);
    if (pct > 80) {
      warnings.push({ label: 'AI Tokens', percent: Math.round(pct), projected: proj?.label || null });
    }
  }
  if (transcriptLimit && transcriptLimit > 0) {
    const pct = (usage.totals.transcriptsProcessed / transcriptLimit) * 100;
    const proj = getProjection(usage.totals.transcriptsProcessed, transcriptLimit);
    if (pct > 80) {
      warnings.push({ label: 'Transcripts', percent: Math.round(pct), projected: proj?.label || null });
    }
  }

  if (warnings.length === 0) return null;

  return (
    <div className="mb-2 flex flex-col gap-2">
      {warnings.map(w => (
        <Alert key={w.label} variant={w.percent >= 95 ? 'error' : 'warning'}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <AlertDescription>
              <strong>{w.label}:</strong> {w.percent}% of monthly quota used
              {w.projected && <span className="mt-0.5 block text-xs">{w.projected}</span>}
            </AlertDescription>
          </div>
        </Alert>
      ))}
    </div>
  );
}

function UsageProjectionCard({ usage }: { usage: UsageData }) {
  const aiProj = getProjection(usage.totals.aiTokensUsed, usage.limits.monthlyAiTokenLimit);
  const transcriptProj = getProjection(usage.totals.transcriptsProcessed, usage.limits.monthlyTranscriptLimit);

  if (!aiProj && !transcriptProj) return null;

  return (
    <div className="mt-2 rounded-md border border-border bg-muted/30 p-3">
      <p className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Month-End Projection
      </p>
      <div className="flex flex-col gap-0.5">
        {aiProj && (
          <p
            className={cn(
              'text-sm',
              aiProj.projectedPercent > 100 ? 'text-rose-500'
                : aiProj.projectedPercent > 80 ? 'text-amber-500'
                : '',
            )}
          >
            AI Tokens: ~{aiProj.projectedPercent}% of limit
          </p>
        )}
        {transcriptProj && (
          <p
            className={cn(
              'text-sm',
              transcriptProj.projectedPercent > 100 ? 'text-rose-500'
                : transcriptProj.projectedPercent > 80 ? 'text-amber-500'
                : '',
            )}
          >
            Transcripts: ~{transcriptProj.projectedPercent}% of limit
          </p>
        )}
      </div>
    </div>
  );
}

function UsageTrendChip({ label, current, previous }: { label: string; current: number; previous: number }) {
  if (previous <= 0) return null;
  const changePercent = Math.round(((current - previous) / previous) * 100);
  if (changePercent === 0) return null;
  const isUp = changePercent > 0;
  const Icon = isUp ? TrendingUp : TrendingDown;
  return (
    <Badge
      variant="secondary"
      className={cn(
        'gap-1 font-medium',
        isUp ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
      )}
    >
      <Icon className="h-3 w-3" />
      {label}: {isUp ? '+' : ''}{changePercent}% vs last month
    </Badge>
  );
}

export default function TenantBillingPage() {
  const entitlements = useEntitlements();
  const { refresh: refreshEntitlements } = entitlements;
  // Coming back from the Stripe portal is when a plan changes: re-read it on
  // arrival and on return to the tab (TenantUpdated only reaches a browser
  // connected to the server that handled the change).
  useEffect(() => {
    void refreshEntitlements();
    const onFocus = () => { void refreshEntitlements(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshEntitlements]);

  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [prevUsage, setPrevUsage] = useState<UsageData | null>(null);
  const [billingEmail, setBillingEmail] = useState('');
  const [billingEmailSaving, setBillingEmailSaving] = useState(false);
  const [billingEmailSuccess, setBillingEmailSuccess] = useState<string | null>(null);

  const [tenantInfo, setTenantInfo] = useState<{
    maxExecutiveSeats: number;
    currentExecutives: number;
    maxEaSeats: number;
    monthlyTranscriptLimit: number;
  } | null>(null);
  const [newSeatCount, setNewSeatCount] = useState<number>(1);
  const [seatUpdating, setSeatUpdating] = useState(false);
  const [seatSuccess, setSeatSuccess] = useState<string | null>(null);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
      const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const [statusResult, usageResult, tenantResult, prevUsageResult] = await Promise.allSettled([
        billingApi.getStatus(),
        tenantAdminApi.getUsage(),
        tenantAdminApi.getTenant(),
        tenantAdminApi.getUsage(prevYear, prevMonth),
      ]);

      if (statusResult.status === 'fulfilled') {
        setBillingStatus(statusResult.value);
      }
      if (usageResult.status === 'fulfilled') {
        setUsage(usageResult.value);
      }
      if (tenantResult.status === 'fulfilled') {
        const t = tenantResult.value;
        if (t?.billingEmail) setBillingEmail(t.billingEmail);
        if (t) {
          setTenantInfo({
            maxExecutiveSeats: t.maxExecutiveSeats,
            currentExecutives: t.currentExecutives,
            maxEaSeats: t.maxEaSeats,
            monthlyTranscriptLimit: t.monthlyTranscriptLimit,
          });
          setNewSeatCount(t.maxExecutiveSeats);
        }
      }

      if (prevUsageResult.status === 'fulfilled') {
        setPrevUsage(prevUsageResult.value);
      }

      if (statusResult.status === 'rejected' && usageResult.status === 'rejected') {
        setError('Failed to load billing information.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const result = await billingApi.getPortalUrl();
      if (result.portalUrl) {
        window.open(result.portalUrl, '_blank');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpdateBillingEmail = async () => {
    if (!billingEmail.trim()) return;
    setBillingEmailSaving(true);
    setBillingEmailSuccess(null);
    try {
      await tenantAdminApi.updateBillingEmail(billingEmail.trim());
      setBillingEmailSuccess('Billing email updated successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update billing email.');
    } finally {
      setBillingEmailSaving(false);
    }
  };

  const handleUpdateSeats = async () => {
    if (newSeatCount < 1) return;
    setSeatUpdating(true);
    setSeatSuccess(null);
    setError(null);
    try {
      const result = await tenantAdminApi.updateSeats(newSeatCount);
      setSeatSuccess(result.message || 'Seats updated successfully.');
      if (tenantInfo) {
        setTenantInfo({
          ...tenantInfo,
          maxExecutiveSeats: result.executiveSeats,
          maxEaSeats: result.eaSeats,
          monthlyTranscriptLimit: result.monthlyTranscriptLimit,
        });
      }
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update seats.');
    } finally {
      setSeatUpdating(false);
    }
  };

  const handleCancelTenant = async () => {
    if (!mfaCode.trim()) return;
    setCancelling(true);
    setError(null);
    try {
      await tenantAdminApi.cancelTenant(mfaCode.trim());
      setCancelDialogOpen(false);
      setMfaCode('');
      setCancelSuccess('Subscription cancelled. Your data will be deleted within 24 hours.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to cancel subscription. Check your MFA code.');
    } finally {
      setCancelling(false);
    }
  };

  const seatDifference = tenantInfo ? newSeatCount - tenantInfo.maxExecutiveSeats : 0;
  const yearly = billingStatus?.interval === 'year';
  // Seats are billed at the chosen interval's price.
  const estimatedCostChange = billingStatus?.pricePerSeat
    ? (seatDifference * billingStatus.pricePerSeat) / 100
    : null;
  const currency = (billingStatus?.currency ?? 'gbp').toUpperCase();
  const viaAppStore = billingStatus?.renewsThroughAppStore === true;
  // Personal is one person: nothing to buy more of (the plan data the app
  // already has decides it if the billing status call failed).
  const showSeats = !(billingStatus?.isSelfServeIndividual ?? entitlements.isSelfServeIndividual);
  const trialDays = billingStatus?.isTrialing ? billingStatus.trialDaysRemaining ?? null : null;

  return (
    <div>
      <PageHeader
        icon={CreditCard}
        title="Billing & Subscription"
        subtitle="Manage your plan, view usage, and update payment details"
      />

      {/* Trial: keyed off the billing status (a Stripe or App Store trial, or
          a platform-started one), never off the plan name. A store trial
          converts on its own; a platform trial just ends. */}
      {trialDays !== null && (
        trialDays <= 0 && !billingStatus?.stripeCustomerId && !viaAppStore ? (
          <Alert variant="error" className="mb-3">
            <AlertDescription>
              Your trial of {planLabel(billingStatus?.plan)} has ended. Contact your account manager to choose a plan.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="mb-3">
            <div className="flex items-center justify-between gap-2">
              <AlertDescription>
                You're on a free trial of {planLabel(billingStatus?.plan)}.{' '}
                <strong>{trialDays} day{trialDays !== 1 ? 's' : ''} remaining.</strong>
                {billingStatus?.stripeCustomerId && ' Your subscription starts when the trial ends.'}
                {viaAppStore && ' Your App Store subscription starts when the trial ends.'}
              </AlertDescription>
              {billingStatus?.stripeCustomerId && (
                <Button size="sm" variant="outline" onClick={handleManageSubscription} disabled={portalLoading}>
                  Manage Subscription
                </Button>
              )}
            </div>
          </Alert>
        )
      )}

      {billingStatus?.seatAccessEndsAt && (
        <Alert variant="warning" className="mb-3">
          <AlertDescription>
            Your plan has fewer seats than your organisation is using. The users over it keep access until{' '}
            <strong>{formatDate(billingStatus.seatAccessEndsAt)}</strong>, then are deactivated (their work is kept).
            Change your plan before then to keep them.
          </AlertDescription>
        </Alert>
      )}

      {loading && <LinearProgressBar className="mb-2" />}
      {error && (
        <Alert variant="error" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* Current Plan */}
        <Card className="h-full">
          <CardContent className="pt-6">
            <h3 className="mb-3 text-base font-semibold">Current Plan</h3>

            {billingStatus?.status === 'NoBilling' ? (
              <div className="py-4 text-center">
                <p className="mb-2 text-sm text-muted-foreground">No billing configured for this tenant.</p>
                <p className="text-sm text-muted-foreground">Contact your platform administrator to set up a subscription.</p>
              </div>
            ) : billingStatus ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-primary">{planLabel(billingStatus.plan) || 'Unknown Plan'}</span>
                  <Badge
                    variant="outline"
                    className={statusTone(billingStatus.status).chip}
                  >
                    {billingStatus.status}
                  </Badge>
                  <Badge variant="outline">{intervalLabel(billingStatus.interval)}</Badge>
                </div>

                {billingStatus.pricePerSeat != null && billingStatus.pricePerSeat > 0 && (
                  <div>
                    <p className="text-sm">
                      {formatPence(billingStatus.pricePerSeat, billingStatus.currency)}
                      {billingStatus.isSelfServeIndividual ? '' : ' per seat'} {yearly ? 'a year' : 'a month'}
                    </p>
                    {!billingStatus.isSelfServeIndividual && (
                      <p className="text-sm text-muted-foreground">
                        {billingStatus.seatCount || 1} seat{(billingStatus.seatCount || 1) !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                )}

                <div className="border-t border-border" />

                {billingStatus.nextRenewalAt && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {billingStatus.isTrialing ? 'First payment' : 'Next renewal'}
                    </p>
                    <p className="mt-1 text-sm">
                      {viaAppStore
                        ? <>Renews through the App Store on {formatDate(billingStatus.nextRenewalAt)}</>
                        : billingStatus.nextRenewalAmountPence != null
                          ? <>{formatDate(billingStatus.nextRenewalAt)} · {formatPence(billingStatus.nextRenewalAmountPence, billingStatus.currency)}</>
                          : formatDate(billingStatus.nextRenewalAt)}
                    </p>
                  </div>
                )}

                {billingStatus.currentPeriodEnd && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Current Period</p>
                    <p className="mt-1 text-sm">
                      {billingStatus.currentPeriodStart
                        ? new Date(billingStatus.currentPeriodStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '---'}
                      {' to '}
                      {new Date(billingStatus.currentPeriodEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                )}

                {billingStatus.stripeCustomerId && (
                  <Button
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    className="mt-1 gap-1.5 self-start"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {portalLoading ? 'Opening...' : 'Manage Subscription'}
                  </Button>
                )}
              </div>
            ) : !loading ? (
              <p className="py-4 text-center text-sm text-muted-foreground">Unable to load billing status.</p>
            ) : null}
          </CardContent>
        </Card>

        {/* Usage This Month */}
        <Card className="h-full">
          <CardContent className="pt-6">
            <div className="mb-3 flex items-center gap-1.5">
              <TrendingUp className="h-5 w-5 text-amber-500" />
              <h3 className="text-base font-semibold">Usage This Month</h3>
            </div>

            {usage ? (
              <div className="flex flex-col gap-3">
                <QuotaWarningBars usage={usage} />
                {prevUsage && (
                  <div className="flex flex-wrap gap-1.5">
                    <UsageTrendChip label="AI Tokens" current={usage.totals.aiTokensUsed} previous={prevUsage.totals.aiTokensUsed} />
                    <UsageTrendChip label="Transcripts" current={usage.totals.transcriptsProcessed} previous={prevUsage.totals.transcriptsProcessed} />
                    <UsageTrendChip label="Plans" current={usage.totals.planGenerations} previous={prevUsage.totals.planGenerations} />
                  </div>
                )}
                <UsageBar
                  label="AI Operations"
                  current={usage.totals.aiOperations ?? 0}
                  limit={usage.limits.monthlyAiOperations}
                />
                {usage.limits.aiOperationsPerSeat != null && usage.limits.executiveSeats != null && (
                  <p className="block text-xs text-muted-foreground" style={{ marginTop: '-0.5rem' }}>
                    ({usage.limits.aiOperationsPerSeat} per executive seat × {usage.limits.executiveSeats}. Everything keeps working past it; you'll get a notice at 80% and 100%.)
                  </p>
                )}
                <UsageBar label="Plan Generations" current={usage.totals.planGenerations} />
                <UsageBar
                  label="Transcripts Processed"
                  current={usage.totals.transcriptsProcessed}
                  limit={usage.limits.monthlyTranscriptLimit}
                />
                {usage.limits.transcriptsPerSeat != null && usage.limits.executiveSeats != null && (
                  <p className="block text-xs text-muted-foreground" style={{ marginTop: '-0.5rem' }}>
                    ({usage.limits.transcriptsPerSeat} per executive seat × {usage.limits.executiveSeats})
                  </p>
                )}
                <UsageBar
                  label="AI Tokens Used"
                  current={usage.totals.aiTokensUsed}
                  limit={usage.limits.monthlyAiTokenLimit}
                  unlimitedNote={usage.aiProvider?.source === 'tenant'
                    ? `Unlimited — using your own ${usage.aiProvider.provider === 'Foundry' ? 'Microsoft Foundry' : usage.aiProvider.provider} key`
                    : undefined}
                />
                <UsageBar label="Image Analyses" current={usage.totals.imageAnalyses} />
                <UsageBar label="Work Items Created" current={usage.totals.workItemsCreated} />
                <UsageProjectionCard usage={usage} />
              </div>
            ) : !loading ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No usage data available.</p>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Manage Seats — not for a one-person plan (Personal) */}
      {showSeats && (
      <Card className="mt-3">
        <CardContent className="pt-6">
          <h3 className="mb-3 text-base font-semibold">Manage Seats</h3>
          {seatSuccess && (
            <Alert variant="success" className="mb-3">
              <AlertDescription>{seatSuccess}</AlertDescription>
            </Alert>
          )}
          {viaAppStore ? (
            <p className="text-sm text-muted-foreground">
              An App Store subscription covers one executive seat. Contact support to move to a multi-seat plan.
            </p>
          ) : tenantInfo ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                Current seats: {tenantInfo.maxExecutiveSeats} executive, {tenantInfo.maxEaSeats} EA
                {' '}({tenantInfo.currentExecutives} active executive{tenantInfo.currentExecutives !== 1 ? 's' : ''})
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setNewSeatCount(Math.max(1, newSeatCount - 1))}
                  disabled={newSeatCount <= 1 || seatUpdating}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  type="number"
                  value={newSeatCount}
                  onChange={e => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 1 && val <= 500) setNewSeatCount(val);
                  }}
                  min={1}
                  max={500}
                  disabled={seatUpdating}
                  className="w-20 text-center"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setNewSeatCount(Math.min(500, newSeatCount + 1))}
                  disabled={newSeatCount >= 500 || seatUpdating}
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">executive seats</span>
              </div>
              {seatDifference !== 0 && (
                <div>
                  {estimatedCostChange !== null && (
                    <p
                      className={cn(
                        'text-sm',
                        seatDifference > 0 ? 'text-amber-500' : 'text-emerald-500',
                      )}
                    >
                      {seatDifference > 0 ? 'Adding' : 'Removing'} {Math.abs(seatDifference)} seat{Math.abs(seatDifference) !== 1 ? 's' : ''}
                      {' = '}{seatDifference > 0 ? '+' : '-'}{new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Math.abs(estimatedCostChange))}{yearly ? '/yr' : '/mo'}
                    </p>
                  )}
                  {newSeatCount < (tenantInfo?.currentExecutives ?? 0) && (
                    <Alert variant="warning" className="mt-2">
                      <AlertDescription>
                        Cannot reduce below {tenantInfo.currentExecutives} seats — you have {tenantInfo.currentExecutives} active executive{tenantInfo.currentExecutives !== 1 ? 's' : ''}.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
              <Button
                onClick={handleUpdateSeats}
                disabled={seatUpdating || seatDifference === 0 || newSeatCount < (tenantInfo?.currentExecutives ?? 0)}
                className="self-start"
              >
                {seatUpdating ? 'Updating...' : 'Update Seats'}
              </Button>
            </div>
          ) : !loading ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Unable to load seat information.</p>
          ) : null}
        </CardContent>
      </Card>
      )}

      {/* Billing Email */}
      <Card className="mt-3">
        <CardContent className="pt-6">
          <h3 className="mb-3 text-base font-semibold">Billing Email</h3>
          {billingEmailSuccess && (
            <Alert variant="success" className="mb-3">
              <AlertDescription>{billingEmailSuccess}</AlertDescription>
            </Alert>
          )}
          <div className="flex items-end gap-2">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="billing-email">Billing Email</Label>
              <Input
                id="billing-email"
                type="email"
                value={billingEmail}
                onChange={e => setBillingEmail(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">This email receives all invoices and billing communications</p>
            </div>
            <Button
              onClick={handleUpdateBillingEmail}
              disabled={billingEmailSaving || !billingEmail.trim()}
              className="min-w-40"
            >
              {billingEmailSaving ? 'Saving...' : 'Update Billing Email'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payment & Invoices */}
      {billingStatus?.stripeCustomerId && (
        <Card className="mt-3">
          <CardContent className="pt-6">
            <h3 className="mb-3 text-base font-semibold">Payment & Invoices</h3>
            <p className="mb-3 text-sm text-muted-foreground">
              Manage your payment method, view invoices, and download receipts through the Stripe Customer Portal.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleManageSubscription}
                disabled={portalLoading}
                className="gap-1.5"
              >
                <CreditCard className="h-4 w-4" />
                {portalLoading ? 'Opening...' : 'Manage Payment Method'}
              </Button>
              <Button
                variant="outline"
                onClick={handleManageSubscription}
                disabled={portalLoading}
                className="gap-1.5"
              >
                <Receipt className="h-4 w-4" />
                {portalLoading ? 'Opening...' : 'View Invoices'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cancel Subscription */}
      {billingStatus?.stripeCustomerId && billingStatus?.status !== 'Cancelled' && (
        <Card className="mt-3 border-rose-500/20">
          <CardContent className="pt-6">
            <div className="mb-2 flex items-center gap-1.5">
              <AlertTriangle className="h-5 w-5 text-rose-500" />
              <h3 className="text-base font-semibold text-rose-500">Cancel Subscription</h3>
            </div>
            <p className="mb-3 text-sm text-muted-foreground">
              Cancelling your subscription will terminate access for all users in your organisation.
              Your data will be permanently deleted within 24 hours. This action cannot be undone.
            </p>
            {cancelSuccess && (
              <Alert variant="success" className="mb-3">
                <AlertDescription>{cancelSuccess}</AlertDescription>
              </Alert>
            )}
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(true)}
              className="gap-1.5 border-rose-500/40 text-rose-500 hover:bg-rose-500/10"
            >
              <XCircle className="h-4 w-4" />
              Cancel Subscription
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-rose-500">Cancel Subscription</DialogTitle>
          </DialogHeader>
          <Alert variant="error">
            <AlertDescription>
              This will permanently cancel your subscription and all organisation data will be deleted within 24 hours.
              This action cannot be undone.
            </AlertDescription>
          </Alert>
          <p className="text-sm text-muted-foreground">Enter your MFA code to confirm cancellation:</p>
          <Input
            value={mfaCode}
            onChange={e => setMfaCode(e.target.value)}
            placeholder="Enter 6-digit code from your authenticator app"
            maxLength={6}
            autoFocus
            className="text-center font-mono text-lg tracking-[0.3em]"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setCancelDialogOpen(false); setMfaCode(''); }}>
              Keep Subscription
            </Button>
            <Button
              onClick={handleCancelTenant}
              disabled={cancelling || mfaCode.trim().length < 6}
              className="bg-rose-500 text-white hover:bg-rose-600"
            >
              {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Alert className="mt-4">
        <AlertDescription>
          {viaAppStore ? (
            <>Your subscription is billed by Apple. To change or cancel it, open <strong>Settings → your name → Subscriptions</strong> on your iPhone, iPad or Mac.</>
          ) : (
            <>Need to update your payment method, view invoices, or change your plan or billing interval? Use <strong>Manage Subscription</strong> to open the Stripe Customer Portal.</>
          )}
        </AlertDescription>
      </Alert>
    </div>
  );
}
